import React, { ChangeEvent, useEffect, useState } from 'react';
import { ICreditCard } from '../../cart-bridger-pay/interfaces';
import { UPDATE_SAVED_CREDIT_CARDS_EVENT } from '../domain/saved-credit-cards-provider';
import { getSavedCreditCards } from '../../cart-bridger-pay/data/data-service';
import Preloader from '../../../../ui/svg/common/preloader';
import SavedCreditCardShared from './saved-credit-card';
import { Popup } from '../../../../ui/popup/popup';
import { IFormControl } from '../../../../ui/forms/interfaces';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';
import { getHiddenDataBoolean } from '../../../../domain/hidden-data-provider';

/**
 * SAVED CREDIT CARDS -------------------------------------
 * This component is shared in multiple shopping carts like:
 * - main cart
 * - change subscription payment method
 * - my credit cards in account
 * All params should be passed as props without the Redux.
 */

interface ISavedCreditCardsShared {
    isSubscription: boolean;

    savedCreditCards: ICreditCard[];
    savedCreditCardToken: string;
    currentPaymentMethodToken?: string;

    updateSavedCards: (_savedCards: ICreditCard[]) => void;
    selectSavedCard: (savedCard: ICreditCard) => void;

    cvvControl?: IFormControl;
    cvvChanged?: (evt: ChangeEvent<HTMLInputElement>) => void;

    handleAddNewCardButton: () => void;
    showCvvInfoPopup: () => void;

    hideRemoveButtons?: boolean;
    showEditButtons?: boolean;

    onEditClick?: (savedCard: ICreditCard) => void;
}

const filterCreditCards = (savedCreditCards: ICreditCard[], isSubscription: boolean, currentPaymentMethodToken?: string) => {
    let filtered = savedCreditCards;

    if(currentPaymentMethodToken) {
        filtered = filtered.filter(item => item.token !== currentPaymentMethodToken);
    }

    const sbsDebitAllowed = getHiddenDataBoolean('sbs-debit-allowed');

    return (isSubscription && !sbsDebitAllowed) ?
        filtered.filter(item => item.card?.type?.toLowerCase() !== 'debit' &&
                                item.card?.type?.toLowerCase() !== 'prepaid') :
        filtered;
};

const SavedCreditCardsShared = (props: ISavedCreditCardsShared) => {

    const [inUsePopupShown, setInUsePopupShown] = useState(false);

    const {
        isSubscription , savedCreditCards,
        updateSavedCards, handleAddNewCardButton, selectSavedCard,
        showCvvInfoPopup,
        savedCreditCardToken,
    } = props;

    const creditCards = filterCreditCards(savedCreditCards, isSubscription, props.currentPaymentMethodToken);

    const [isLoading, setIsLoading] = useState(false);

    /**
     * Update saved credit cards list from outside
     */
    useEffect(() => {

        const updateSavedCardsFromOutside = (evt: Event) => {

            const detail = (evt as CustomEvent)?.detail;
            if(!detail) return;

            updateSavedCards(detail);
        };

        document.addEventListener(UPDATE_SAVED_CREDIT_CARDS_EVENT, updateSavedCardsFromOutside);

        return () => {
            document.removeEventListener(UPDATE_SAVED_CREDIT_CARDS_EVENT, updateSavedCardsFromOutside);
        };
    }, [updateSavedCards]);

    /**
     * load saved cards data on component init
     */
    useEffect(() => {

        let isUnmount = false;

        (async () => {

            setIsLoading(true);

            try{
                const response = await getSavedCreditCards();

                if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){
                    setIsLoading(false);
                    updateSavedCards([]);
                    return;
                }

                const _savedCards = response.data.relatedData as ICreditCard[];
                const _filtered = filterCreditCards(_savedCards, isSubscription, props.currentPaymentMethodToken);
                updateSavedCards(_filtered);
            }
            catch(ex){
                console.log('Saved credit cards loading error', ex);
            }

            if(isUnmount) return;
            setIsLoading(false);

        })();

        return () => {
            isUnmount = true;
        };
    },
        // eslint-disable-next-line
        []
    );

    const showCartInUsePopup = () => {
        setInUsePopupShown(true);
    };

    const hideCartInUsePopup = () => {
        setInUsePopupShown(false);
    };

    const addNewCardOnClick = ()=> {
        sendGAEvent('Click', 'add new card');

        if(!handleAddNewCardButton) return;

        handleAddNewCardButton();
    };

    return (
        <div>
            {
                isLoading &&
                <div className="flex justify-center">
                    <Preloader width="25px" height="25px" color="#4f4f4f" />
                </div>
            }

            {
                !isLoading && savedCreditCards &&
                <>
                    <button
                        type="button"
                        data-qa="cart-step3-add-new-card-link"
                        className="text-tiny font-bold text-blue-200 mb-4"
                        onClick={ addNewCardOnClick }>
                        + Add New Card
                    </button>

                    {
                        creditCards.map(savedCard => {

                            return (
                                <SavedCreditCardShared
                                    key={ savedCard.token }
                                    savedCard={ savedCard }
                                    isSelected={ savedCreditCardToken === savedCard.token }
                                    cvvControl={ props.cvvControl }
                                    cvvChanged={ props.cvvChanged }
                                    showPopup={ showCartInUsePopup }
                                    showCvvInfoPopup={ showCvvInfoPopup }
                                    selectSavedCard={ selectSavedCard }
                                    hideRemoveButton={ props.hideRemoveButtons }
                                    showEditButton={ props.showEditButtons }
                                    onEditClick={ props.onEditClick }
                                />
                            )
                        })
                    }
                </>
            }

            {
                inUsePopupShown &&
                <Popup width="290px" height="200px" close={ hideCartInUsePopup }>
                    <div className="flex flex-col justify-center items-center w-full h-full text-center">
                        <p className="mb-4">Your credit card can't be removed, it's used by your Waves subscription.</p>
                        <button className="w-btn" type="button" onClick={ hideCartInUsePopup }>OK</button>
                    </div>
                </Popup>
            }
        </div>
    )
};

export default SavedCreditCardsShared;